import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}
import {
  Brain, Shield, BarChart2,
  Activity,
  Star, ChevronDown, ChevronUp, ArrowRight,
  Award, Clock, X, RefreshCw, Menu,
  ChevronLeft, ChevronRight,
} from 'lucide-react';

const AC  = '#6366F1';   // brand accent — single source
const AC2 = '#4F46E5';   // accent hover / deeper shade

// Seeded random — stable positions, sizes 40% smaller than before
const PARTICLES = Array.from({ length: 55 }, (_, i) => {
  const s  = Math.sin(i * 9301 + 49297) * 233280;
  const r  = s  - Math.floor(s);
  const s2 = Math.sin(i * 7919 + 12345) * 233280;
  const r2 = s2 - Math.floor(s2);
  const s3 = Math.sin(i * 6271 + 99991) * 233280;
  const r3 = s3 - Math.floor(s3);
  const s4 = Math.sin(i * 4523 + 77777) * 233280;
  const r4 = s4 - Math.floor(s4);
  // duration 8–18s, delay 0–10s — each particle independent
  return { x: r * 100, y: r2 * 100, size: 0.9 + r3 * 1.2, dur: 16 + r4 * 20, delay: r * 10 };
});

// CSS keyframes injected once
const PARTICLE_CSS = `
@keyframes floatUp {
  0%   { transform: translateY(0);    opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 0.6; }
  100% { transform: translateY(-110vh); opacity: 0; }
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes fadeInLeft {
  from { opacity: 0; transform: translateX(-20px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes subtlePulse {
  0%, 100% { box-shadow: 0 0 40px rgba(99,102,241,0.2), 0 0 80px rgba(99,102,241,0.08); }
  50%       { box-shadow: 0 0 55px rgba(99,102,241,0.32), 0 0 100px rgba(99,102,241,0.14); }
}
.fade-in-up   { animation: fadeInUp  0.6s ease both; }
.fade-in-left { animation: fadeInLeft 0.5s ease both; }
.card-hover { transition: all 0.22s ease; }
.card-hover:hover { transform: translateY(-3px); }
.icon-glow-green  { filter: drop-shadow(0 0 6px rgba(22,199,132,0.6)); }
.icon-glow-red    { filter: drop-shadow(0 0 6px rgba(239,68,68,0.6)); }
.icon-glow-indigo { filter: drop-shadow(0 0 6px rgba(99,102,241,0.7)); }
.icon-glow-amber  { filter: drop-shadow(0 0 6px rgba(245,158,11,0.6)); }
.score-glow       { text-shadow: 0 0 12px rgba(99,102,241,0.6); }
.star-glow        { filter: drop-shadow(0 0 4px rgba(251,191,36,0.7)); }
`;

export default function LandingPage() {
  const navigate   = useNavigate();
  const [faqOpen,         setFaqOpen]         = useState(null);
  const [scrolled,        setScrolled]        = useState(false);
  const [billingPeriod,   setBillingPeriod]   = useState('monthly');
  const [menuOpen,        setMenuOpen]        = useState(false);
  const [carouselActive,  setCarouselActive]  = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError,   setCheckoutError]   = useState('');

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId, t = 0;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      t += 1;

      // Diagonal baseline: bottom-left (0, 78% H) → top-right (W, 22% H)
      // Two lines close together, opposite phase so they intersect
      const lines = [
        { offset: +28, phase: 0,        color: '#6366f1' },
        { offset: -28, phase: Math.PI,  color: '#8b5cf6' },
      ];

      lines.forEach(ln => {
        ctx.beginPath();
        for (let x = 0; x <= W; x += 4) {
          const px   = x / W;
          const base = H * 0.78 - px * H * 0.56;           // diagonal slope
          const wave = 55 * Math.sin(px * Math.PI * 2 * 0.9 + t * 0.0004 + ln.phase);
          const y    = base + ln.offset + wave;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }

        const grad = ctx.createLinearGradient(0, 0, W, 0);
        grad.addColorStop(0,   `${ln.color}00`);
        grad.addColorStop(0.08,`${ln.color}cc`);
        grad.addColorStop(0.5, `${ln.color}ee`);
        grad.addColorStop(0.92,`${ln.color}cc`);
        grad.addColorStop(1,   `${ln.color}00`);

        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.5;
        ctx.shadowBlur = 20;
        ctx.shadowColor = ln.color;
        ctx.stroke();
        ctx.shadowBlur = 0;
      });

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  const go = (path) => navigate(path);

  const handleCheckout = useCallback(async (tier) => {
    const token = localStorage.getItem('token');
    if (!token) {
      go(`/signup?plan=${tier}&billing=${billingPeriod}`);
      return;
    }
    setCheckoutError('');
    setCheckoutLoading(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Could not load Razorpay. Check your connection.');

      const { data } = await axios.post(
        `${API_URL}/subscriptions/create`,
        { tier, billing: billingPeriod },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const sessionUser = JSON.parse(localStorage.getItem('session_user') || '{}');
      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: 'TradeReflex',
        description: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Plan`,
        image: '/logo192.png',
        prefill: { email: sessionUser.email || '', name: sessionUser.full_name || '' },
        theme: { color: '#6366F1' },
        handler: () => {
          go('/?subscribed=1');
        },
        modal: {
          ondismiss: () => setCheckoutLoading(false),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Something went wrong';
      setCheckoutError(msg);
      setCheckoutLoading(false);
    }
  }, [billingPeriod, navigate]);

  return (
    <div style={{
        backgroundColor: '#060d18',
        color: '#e5e5e5',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      }}
      className="min-h-screen">

      {/* inject keyframe once */}
      <style>{PARTICLE_CSS}</style>

      {/* ── FIXED BG LAYER: grid + ribbon canvas + particles ── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>

        {/* Grid mesh */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }} />

        {/* Flowing ribbon canvas */}
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ opacity: 0.22 }} />


        {/* Animated particles — float upward */}
        {PARTICLES.map((p, i) => (
          <div key={i} style={{
            position: 'absolute',
            left: `${p.x}%`,
            top: `${p.y}%`,
            width:  `${p.size * 2}px`,
            height: `${p.size * 2}px`,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.5)',
            animation: `floatUp ${p.dur}s ${p.delay}s linear infinite`,
          }} />
        ))}
      </div>

      {/* ════ HEADER ════ */}
      <header className="fixed top-0 left-0 right-0"
        style={{
          zIndex: 100,
          backgroundColor: scrolled || menuOpen ? 'rgba(6,13,24,0.95)' : 'transparent',
          backdropFilter: scrolled || menuOpen ? 'blur(20px)' : 'none',
          borderBottom: scrolled || menuOpen ? '1px solid rgba(255,255,255,0.06)' : 'none',
          transition: 'all 0.3s ease',
          overflow: 'visible',
        }}>
          <div className="flex items-center justify-between px-4 sm:px-6 overflow-visible" style={{ height: '48px' }}>
            <Logo size="sm" />

            <nav className="hidden md:flex items-center gap-8">
              {[['Features','#features'],['How it Works','#how-it-works'],['Pricing','#pricing'],['FAQ','#faq']].map(([l, h]) => (
                <a key={l} href={h} className="text-base" style={{ color: '#666', textDecoration: 'none' }}
                  onMouseEnter={e => e.target.style.color='#e5e5e5'}
                  onMouseLeave={e => e.target.style.color='#666'}>{l}</a>
              ))}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <button onClick={() => go('/login')} className="hidden sm:block text-sm px-4 py-2 rounded-lg" style={{ color: '#888' }}
                onMouseEnter={e => e.target.style.color='#fff'}
                onMouseLeave={e => e.target.style.color='#888'}>
                Sign In
              </button>
              <button onClick={() => go('/signup')} className="text-sm px-4 sm:px-5 font-semibold text-white mt-2 md:mt-0"
                style={{
                  borderRadius: '9999px',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10), 0 2px 8px rgba(0,0,0,0.5)',
                  transition: 'all 0.2s',
                  paddingTop: '7px',
                  paddingBottom: '7px',
                }}>
                Get Started
              </button>
              {/* Hamburger — mobile only */}
              <button className="md:hidden p-2 rounded-lg" onClick={() => setMenuOpen(o => !o)}
                style={{ color: '#888', background: 'none', border: 'none', cursor: 'pointer', marginTop: '8px' }}>
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile nav drawer */}
          {menuOpen && (
            <div className="md:hidden px-4 pb-4 flex flex-col gap-1"
              style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              {[['Features','#features'],['How it Works','#how-it-works'],['Pricing','#pricing'],['FAQ','#faq'],['Sign In','/login']].map(([l, h]) => (
                <a key={l} href={h}
                  onClick={() => { setMenuOpen(false); if (h.startsWith('/')) { go(h); } }}
                  className="py-3 px-2 text-sm rounded-lg"
                  style={{ color: '#aaa', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  {l}
                </a>
              ))}
            </div>
          )}
        </header>

        <div style={{ position: 'relative', zIndex: 1 }}>

        {/* ════ HERO ════ */}
        <section className="pt-48 pb-12 md:pt-56 md:pb-16 px-4 sm:px-6 text-center" style={{ position: 'relative' }}>
          <div className="max-w-4xl mx-auto" style={{ position: 'relative', zIndex: 1 }}>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-black leading-tight mb-6"
              style={{
                letterSpacing: '-0.03em',
                background: 'linear-gradient(180deg, #ffffff 0%, #ffffff 40%, #8899bb 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
              Your strategy isn't broken.<br />
              Your decision-making is.
            </h1>

            <p className="text-sm sm:text-base md:text-lg max-w-2xl mx-auto mb-7 md:mb-10" style={{ color: '#666', lineHeight: 1.8 }}>
              TradeReflex rewires how you act in trades — until discipline becomes your reflex, not your fight.
              Stop explaining your losses. Start fixing what causes them.
            </p>

            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fbbf24', display: 'inline-block', animation: 'subtlePulse 2s ease-in-out infinite' }} />
                Beta
              </span>
              <p className="text-xs" style={{ color: '#555' }}>
                TradeReflex is currently open to <span style={{ color: '#888' }}>beta users only</span>. Official launch coming soon.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 md:mb-16">
              <button onClick={() => go('/signup')}
                className="flex items-center gap-2 px-7 py-3.5 font-semibold text-white text-sm"
                style={{
                  borderRadius: '9999px',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
                  border: '1px solid rgba(99,102,241,0.4)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 0 20px rgba(99,102,241,0.25), 0 4px 16px rgba(0,0,0,0.4)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(99,102,241,0.7)'; e.currentTarget.style.boxShadow='inset 0 1px 0 rgba(255,255,255,0.16), 0 0 32px rgba(99,102,241,0.4), 0 6px 20px rgba(0,0,0,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(99,102,241,0.4)'; e.currentTarget.style.boxShadow='inset 0 1px 0 rgba(255,255,255,0.12), 0 0 20px rgba(99,102,241,0.25), 0 4px 16px rgba(0,0,0,0.4)'; }}>
                Get Your First Trade Score — Free <ArrowRight className="w-4 h-4" />
              </button>
            </div>

          </div>
        </section>

        {/* ════ PROBLEM ════ */}
        <section id="features" className="py-16 md:py-24 px-4 sm:px-6" style={{ background: 'rgba(0,0,0,0.5)', borderTop: '1px solid rgba(99,102,241,0.15)', borderBottom: '1px solid rgba(99,102,241,0.1)' }}>
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <p className="text-xs font-semibold mb-3 uppercase tracking-widest" style={{ color: AC }}>Why traders fail</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-6" style={{ lineHeight: 1.2 }}>
                The market didn't beat you.<br />
                <span style={{ color: '#666' }}>Your psychology did.</span>
              </h2>
              <p className="mb-5 md:mb-8 text-sm md:text-base" style={{ color: '#777', lineHeight: 1.8 }}>
                Most traders don't fail only because of bad stock picks. They fail because of what happens after — revenge trades, moved stop-losses, overtrading after one win. Every bad habit compounding on the last.
              </p>
              <button onClick={() => go('/signup')} className="flex items-center gap-2 text-sm font-semibold"
                style={{ color: AC, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                onMouseEnter={e => e.currentTarget.style.color='#a5b4fc'}
                onMouseLeave={e => e.currentTarget.style.color=AC}>
                Fix your behavior first <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2.5">
              {[
                'You enter trades without a clear plan',
                'You move stop losses when price goes against you',
                'You overtrade after one big win',
                'You revenge trade after every loss',
                'You exit winners early, hold losers too long',
                "You know all the rules — you just don't follow them",
              ].map((item, i) => (
                <div key={i} className="card-hover flex items-center gap-3 p-3.5 rounded-xl"
                  style={{ backgroundColor: '#0f0f0f', border: '1px solid rgba(255,255,255,0.05)', transition: 'all 0.22s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(239,68,68,0.3)'; e.currentTarget.style.boxShadow='0 0 16px rgba(239,68,68,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.05)'; e.currentTarget.style.boxShadow='none'; }}>
                  <X className="icon-glow-red w-3.5 h-3.5 flex-shrink-0" style={{ color: '#EF4444' }} />
                  <span className="text-sm" style={{ color: '#aaa' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════ DISCIPLINE GAP ════ */}
        <section className="py-16 md:py-24 px-4 sm:px-6" style={{ background: 'linear-gradient(180deg, rgba(99,102,241,0.04) 0%, transparent 60%)', position: 'relative', overflow: 'hidden' }}>
          <div className="absolute pointer-events-none" style={{ top: '5%', left: '-10%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 65%)', filter: 'blur(60px)' }} />
          <div className="absolute pointer-events-none" style={{ bottom: '10%', right: '-10%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 65%)', filter: 'blur(60px)' }} />
          <div className="max-w-5xl mx-auto" style={{ position: 'relative' }}>

            <div className="text-center mb-8 md:mb-14">
              <p className="text-xs font-semibold mb-2 md:mb-4 uppercase tracking-widest" style={{ color: '#EF4444' }}>The real cost</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4" style={{ lineHeight: 1.2 }}>
                93% of F&O traders lose money.
              </h2>
              <p className="text-xs sm:text-sm max-w-2xl mx-auto mb-2 md:mb-3" style={{ color: '#666' }}>
                SEBI's study across FY22–FY24 found 93% of individual F&O traders lost money — aggregate losses crossed <span style={{ color: '#aaa' }}>₹1.8 lakh crore</span> in just three years. For equity intraday, 71% of retail traders lost money in FY23 — rising to 80% among very active traders.
              </p>
              <p className="text-xs" style={{ color: '#666' }}>Sources: SEBI Study on Equity F&O (Sep 2024) · SEBI Intraday Study (Jul 2024)</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-14">
              {[
                { stat: '93%',         label: 'of F&O traders lost money',          source: 'SEBI FY22–24' },
                { stat: '₹1.8L Cr',    label: 'aggregate F&O losses in 3 years',    source: 'SEBI FY22–24' },
                { stat: '71%',         label: 'of intraday traders lost money',      source: 'SEBI FY23'    },
              ].map(s => (
                <div key={s.stat} className="p-3 sm:p-5 rounded-2xl text-center"
                  style={{ backgroundColor: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)' }}>
                  <p className="text-xl sm:text-3xl font-black mb-1" style={{ color: '#EF4444' }}>{s.stat}</p>
                  <p className="text-[10px] sm:text-xs mb-1 sm:mb-2" style={{ color: '#aaa', lineHeight: 1.5 }}>{s.label}</p>
                  <p className="text-[9px] sm:text-[11px] font-semibold uppercase tracking-widest" style={{ color: '#555' }}>{s.source}</p>
                </div>
              ))}
            </div>

            <div className="text-center mb-8">
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-white" style={{ lineHeight: 1.4 }}>
                The market is already hard.<br />
                <span style={{ color: '#666' }}>Your behavior shouldn't make it harder.</span>
              </p>
            </div>
            <div className="mb-10 grid grid-cols-2 gap-4">
              <div className="rounded-2xl p-5 text-center"
                style={{ backgroundColor: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.3)', boxShadow: '0 0 24px rgba(239,68,68,0.12), inset 0 0 24px rgba(239,68,68,0.04)' }}>
                <p className="text-xs font-semibold uppercase tracking-wide mb-4" style={{ color: '#EF4444' }}>Before TradeReflex</p>
                <ul>
                  {['Skipped stop-losses','Emotion-driven entries','Uncontrolled risk sizing','FOMO / revenge trades','No post-trade feedback'].map((item, i, arr) => (
                    <li key={item} className="flex items-center justify-center gap-2 text-xs py-2.5" style={{ color: '#cc4444', borderTop: '1px solid rgba(239,68,68,0.15)', borderBottom: i === arr.length - 1 ? '1px solid rgba(239,68,68,0.15)' : 'none' }}>
                      <span style={{ color: '#EF4444', fontWeight: 700 }}>✕</span>{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl p-5 text-center"
                style={{ backgroundColor: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.35)', boxShadow: '0 0 24px rgba(99,102,241,0.15), inset 0 0 24px rgba(99,102,241,0.04)' }}>
                <p className="text-xs font-semibold uppercase tracking-wide mb-4" style={{ color: AC }}>With TradeReflex</p>
                <ul>
                  {['Stop-loss tracked in Discipline Score','Entries scored before & after trade','Position size guided by risk profile','Nyx flags emotional re-entries','AI debrief after every closed trade'].map((item, i, arr) => (
                    <li key={item} className="flex items-center justify-center gap-2 text-xs py-2.5" style={{ color: '#a0a8c0', borderTop: '1px solid rgba(99,102,241,0.15)', borderBottom: i === arr.length - 1 ? '1px solid rgba(99,102,241,0.15)' : 'none' }}>
                      <span style={{ color: '#16C784', fontWeight: 700 }}>✓</span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-4 rounded-2xl p-4 sm:p-6 text-center"
              style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(99,102,241,0.03) 100%)', border: '1px solid rgba(99,102,241,0.2)' }}>
              <p className="text-xs sm:text-sm" style={{ color: '#777' }}>
                Every session tracked. Every pattern identified. Every improvement measured — so you can see exactly where you stand and what to fix next with TradeReflex.
              </p>
            </div>

          </div>
        </section>

        {/* ════ NYX AI ════ */}
        <section className="py-16 md:py-24 px-4 sm:px-6" style={{ background: 'linear-gradient(180deg, rgba(99,102,241,0.04) 0%, transparent 60%)', position: 'relative', overflow: 'hidden' }}>
          <div className="absolute pointer-events-none" style={{ top: '10%', left: '-10%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 65%)', filter: 'blur(60px)' }} />
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 md:gap-16 items-center" style={{ position: 'relative' }}>

            {/* LEFT — text */}
            <div className="order-1">
              <p className="text-xs font-semibold mb-3 uppercase tracking-widest" style={{ color: AC }}>Meet Nyx AI</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-6" style={{ lineHeight: 1.2 }}>
                Your AI Discipline Coach
              </h2>
              <p className="mb-5 md:mb-8 text-sm md:text-base" style={{ color: '#777', lineHeight: 1.8 }}>
                Nyx uses your trade history, ACT answers, risk profile, and Discipline Score to give feedback specific to your trading behavior.
              </p>
              <div className="space-y-3">
                {[
                  { icon: Brain,     text: 'Pre-trade: checks R:R, stop-loss, position size, and risk rules before entry.' },
                  { icon: Activity,  text: 'Behavioral: spots FOMO, revenge trading, overtrading, and confidence gaps.' },
                  { icon: RefreshCw, text: 'Post-trade: turns each closed trade into a short debrief with one fix for the next session.' },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-start gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: 'rgba(99,102,241,0.1)' }}>
                      <Icon className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: AC }} />
                    </div>
                    <span className="text-xs sm:text-sm" style={{ color: '#777' }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — chat UI */}
            <div className="order-2 rounded-2xl p-3 md:p-4" style={{ backgroundColor: '#080f1c', border: '1px solid rgba(255,255,255,0.09)' }}>
              <div className="flex items-center gap-2 mb-3 pb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: AC2 }}>
                  <Brain className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">Nyx AI</p>
                  <p className="text-[11px] md:text-[13px]" style={{ color: '#777' }}>Your AI Discipline Coach</p>
                </div>
                <span className="ml-auto text-[11px] md:text-[13px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#22c55e18', color: '#16C784' }}>● Active</span>
              </div>
              <div className="space-y-2.5">
                <div className="flex justify-end">
                  <div className="max-w-[85%] px-3 py-2 rounded-2xl rounded-tr-sm text-xs md:text-sm" style={{ backgroundColor: '#0e1828', color: '#ccc' }}>
                    Analyze my last 10 trades and tell me my mistakes.
                  </div>
                </div>
                <div className="flex gap-2 items-start">
                  <div className="w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: AC2 }}>
                    <Brain className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" />
                  </div>
                  <div className="max-w-[85%] px-3 py-2 rounded-2xl rounded-tl-sm text-xs md:text-sm" style={{ backgroundColor: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.18)', color: '#ccc' }}>
Your last 10 trades show low R:R and higher frequency after losses. For the next session, take only setups above 2:1 R:R and cap yourself at 3 trades.
                  </div>
                </div>
                {/* second exchange — hidden on mobile to keep section compact */}
                <div className="hidden md:flex justify-end">
                  <div className="max-w-[85%] px-3 py-2 rounded-2xl rounded-tr-sm text-sm" style={{ backgroundColor: '#0e1828', color: '#ccc' }}>
                    How do I fix this and start making more positive trades?
                  </div>
                </div>
                <div className="hidden md:flex gap-2 items-start">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: AC2 }}>
                    <Brain className="w-3 h-3 text-white" />
                  </div>
                  <div className="max-w-[85%] px-3 py-2 rounded-2xl rounded-tl-sm text-sm" style={{ backgroundColor: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.18)', color: '#ccc' }}>
                    <span style={{ color: '#a78bfa' }}>🧠</span> You're an <span style={{ color: '#a78bfa', fontWeight: 600 }}>impulsive signal-chaser</span> — Only trade when R:R ≥ 2:1. Every SKIP is capital protected.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════ COMPLETE LOOP ════ */}
        <section id="how-it-works" className="py-16 md:py-24 px-4 sm:px-6" style={{ background: 'rgba(0,0,0,0.5)', borderTop: '1px solid rgba(99,102,241,0.15)', borderBottom: '1px solid rgba(99,102,241,0.1)', position: 'relative', overflow: 'hidden' }}>
          <div className="absolute pointer-events-none" style={{ top: '-20%', right: '-5%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 65%)', filter: 'blur(70px)' }} />
          <div className="max-w-5xl mx-auto" style={{ position: 'relative' }}>
            <div className="text-center mb-8 md:mb-14">
              <p className="text-xs font-semibold mb-2 md:mb-4 uppercase tracking-widest" style={{ color: AC }}>The complete loop</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 md:mb-3" style={{ lineHeight: 1.25 }}>
                TradeReflex — a complete improvement cycle
              </h2>
              <p className="text-xs sm:text-sm" style={{ color: '#666' }}>Learn all this without risking a single real rupee.</p>
            </div>

            <div className="overflow-x-auto pb-2">
              <div className="grid grid-cols-5 gap-0 min-w-[700px]">
                {[
                  { num: '01', title: 'Learn',   color: '#6366F1', items: ['Trading rules', 'Risk profile', 'ACT scenarios'] },
                  { num: '02', title: 'Test',    color: '#8b5cf6', items: ['Strategy checks', 'Risk calculator', 'Backtesting'] },
                  { num: '03', title: 'Execute', color: '#a78bfa', items: ['Virtual trades', 'Stop-loss rules', 'Order practice'] },
                  { num: '04', title: 'Improve', color: '#c084fc', items: ['Discipline score', 'Nyx feedback', 'Trade analysis'] },
                  { num: '05', title: 'Review',  color: '#16C784', items: ['Behavior patterns', 'Progress reports', 'Next-session fixes'] },
                ].map((col, i, arr) => (
                  <div key={col.title} className="relative flex flex-col"
                    style={{
                      borderLeft: i === 0 ? `2px solid ${col.color}55` : 'none',
                      borderRight: `2px solid ${col.color}55`,
                      borderTop: `2px solid ${col.color}55`,
                      borderBottom: `2px solid ${col.color}55`,
                      borderRadius: i === 0 ? '12px 0 0 12px' : i === arr.length - 1 ? '0 12px 12px 0' : '0',
                      background: `linear-gradient(180deg, ${col.color}0d 0%, transparent 100%)`,
                    }}>
                    <div className="h-1 w-full rounded-t-sm" style={{ backgroundColor: col.color, opacity: 0.6 }} />
                    <div className="p-4 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black"
                          style={{ backgroundColor: `${col.color}25`, color: col.color, border: `1px solid ${col.color}55` }}>
                          {col.num}
                        </div>
                        <span className="text-sm font-bold text-white">{col.title}</span>
                      </div>
                      <ul className="space-y-2.5 flex-1">
                        {col.items.map(item => (
                          <li key={item} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: col.color }} />
                            <span className="text-xs leading-relaxed" style={{ color: '#aaa' }}>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>

        {/* ════ VIRTUAL LAB ════ */}
        <section className="py-16 md:py-24 px-4 sm:px-6" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(99,102,241,0.05) 50%, transparent 100%)' }}>
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl p-5 sm:p-8 md:p-12 relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(99,102,241,0.03) 50%, transparent 100%)', border: '1px solid rgba(99,102,241,0.18)' }}>
              <div className="absolute top-0 right-0 rounded-full pointer-events-none"
                style={{ width: '350px', height: '350px', background: 'radial-gradient(circle, rgba(129,140,248,0.08), transparent 70%)', filter: 'blur(50px)', transform: 'translate(30%,-30%)' }} />

              {/* Dashboard ghost — decorative background layer */}
              <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.07, padding: '2rem', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px', marginBottom: '8px' }}>
                  {['74', '+₹3,240', '2', '₹42,000'].map(v => (
                    <div key={v} style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', padding: '10px', textAlign: 'center' }}>
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: '13px' }}>{v}</div>
                    </div>
                  ))}
                </div>
                {['RELIANCE  BUY  ₹2,942 → ₹2,978  +₹1,440', 'HDFCBANK  BUY  ₹1,674 → ₹1,691  +₹850', 'INFY  SELL  ₹1,521 → ₹1,509  +₹950'].map(row => (
                  <div key={row} style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '10px 16px', color: '#fff', fontSize: '11px', fontWeight: 600 }}>{row}</div>
                ))}
                {/* gradient mask to fade the ghost */}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(8,15,28,0.6) 0%, transparent 40%, transparent 60%, rgba(8,15,28,0.8) 100%)', borderRadius: 'inherit' }} />
              </div>

              <div className="relative grid md:grid-cols-2 gap-8 md:gap-12 items-center" style={{ zIndex: 1 }}>
                <div>
                  <p className="text-xs font-semibold mb-2 md:mb-4 uppercase tracking-widest" style={{ color: AC }}>Simulated environment. Real market data.</p>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 md:mb-6" style={{ lineHeight: 1.2 }}>
                    Trade with ₹10L<br />of Virtual Capital
                  </h2>
                  <p className="mb-3 md:mb-4 text-sm md:text-base" style={{ color: '#777', lineHeight: 1.8 }}>
                    TradeReflex uses real historical NSE OHLCV data in a simulated trading session. You practice on actual price movement and volume behavior, but no real money or live orders are involved.
                  </p>
                  <p className="mb-4 md:mb-6 text-xs" style={{ color: '#555', lineHeight: 1.8 }}>
                    Virtual trading only. No real-money orders are placed.
                  </p>
                  <button onClick={() => go('/signup')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white"
                    style={{ backgroundColor: AC2 }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor='#4f46e5'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor=AC2}>
                    Start Practising Free <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Virtual Capital',   value: '₹10,00,000',   sub: 'Adjusts daily with your P&L',                                     icon: Award   },
                    { label: 'Capital Control',    value: 'Max ₹2L/day',  sub: 'Earn more capital by trading better — not by depositing more.',   icon: Shield  },
                    { label: 'Equity Trading',     value: 'Free',         sub: '51+ NSE stocks — available on all plans',                        icon: Activity},
                    { label: 'F&O Trading',        value: 'Premium',      sub: 'Options & futures — CE/PE, writing, futures with risk controls',  icon: Clock   },
                  ].map(({ label, value, sub, icon: Icon }) => (
                    <div key={label} className="card-hover p-4 rounded-xl"
                      style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', transition: 'all 0.22s ease' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(99,102,241,0.4)'; e.currentTarget.style.boxShadow='0 0 20px rgba(99,102,241,0.12)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.06)'; e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='translateY(0)'; }}>
                      <Icon className="icon-glow-indigo w-5 h-5 mb-3" style={{ color: AC }} />
                      <p className="text-xs mb-1" style={{ color: '#777' }}>{label}</p>
                      <p className="text-sm font-semibold text-white mb-1">{value}</p>
                      <p className="text-[11px]" style={{ color: '#555', lineHeight: 1.5 }}>{sub}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ════ FEATURES GRID + JOURNEY (combined) ════ */}
        <section id="features-detail" className="py-16 md:py-24 px-4 sm:px-6" style={{ background: 'rgba(0,0,0,0.5)', borderTop: '1px solid rgba(99,102,241,0.15)', borderBottom: '1px solid rgba(99,102,241,0.1)', position: 'relative', overflow: 'hidden' }}>
          <div className="absolute pointer-events-none" style={{ top: '-20%', right: '-5%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 65%)', filter: 'blur(70px)' }} />

          <div className="max-w-5xl mx-auto" style={{ position: 'relative' }}>

            {/* Carousel header */}
            <div className="text-center mb-8 md:mb-12">
              <p className="text-xs font-semibold mb-2 md:mb-4 uppercase tracking-widest" style={{ color: AC }}>In action</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ lineHeight: 1.25 }}>
                See TradeReflex in action
              </h2>
            </div>

            {/* Carousel */}
            {(() => {
              const slides = [
                { img: '/images/disciplinescore.png', color: AC,        title: 'Discipline Score',    verdict: 'Your stop-loss discipline is 45/100.',                            detail: 'You moved or skipped SL in 4 of your last 10 trades. That single habit is costing you more than bad stock picks.',   fix: 'Set SL before entry. Do not touch it after the order fills.' },
                { img: '/images/nyxai.png',           color: '#a78bfa', title: 'Nyx AI Coach',        verdict: 'You revenge traded 3 times this week.',                           detail: 'Nyx reads your last 40 trades, ACT answers, and discipline score — then tells you exactly what pattern to fix next.', fix: 'Pause 20 minutes after every loss. No exceptions.' },
                { img: '/images/tradediscipline.png', color: '#34d399', title: 'Trade Analysis',      verdict: 'This trade had poor R:R — 1.1:1.',                                detail: 'Entry risk was too high relative to the expected reward. Nyx scores every trade before and after it closes.',         fix: 'Only take setups with R:R ≥ 2:1.' },
                { img: '/images/tradepersonality.png',color: '#fb923c', title: 'Behavior Analytics',  verdict: 'Win rate drops to 31% after 3 PM.',                               detail: 'Behavior analytics finds patterns you miss — time of day, trade frequency, strategy tag, confidence vs. outcome.',    fix: 'Stop trading after 2:30 PM. Your edge disappears late session.' },
                { img: '/images/backtest.png',        color: '#38bdf8', title: 'Backtesting',         verdict: 'Your rule-based strategy beat manual trading by 23%.',            detail: 'Run any strategy on real NSE historical data. See what actually works before you risk capital on it.',                fix: 'Backtest first. Trade with conviction, not hope.' },
              ];
              const s = slides[carouselActive];
              const prev = () => setCarouselActive(a => (a - 1 + slides.length) % slides.length);
              const next = () => setCarouselActive(a => (a + 1) % slides.length);
              return (
                <div className="mb-16">
                  <div className="flex gap-2 flex-wrap justify-center mb-6">
                    {slides.map((sl, i) => (
                      <button key={sl.title} onClick={() => setCarouselActive(i)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full transition-all"
                        style={{ backgroundColor: i === carouselActive ? `${sl.color}20` : 'rgba(255,255,255,0.04)', border: `1px solid ${i === carouselActive ? sl.color : 'rgba(255,255,255,0.08)'}`, color: i === carouselActive ? sl.color : '#555' }}>
                        {sl.title}
                      </button>
                    ))}
                  </div>
                  <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${s.color}30`, boxShadow: `0 0 40px ${s.color}15` }}>
                    <div style={{ borderBottom: `1px solid ${s.color}20`, backgroundColor: '#080d18' }}>
                      <img src={s.img} alt={s.title} style={{ width: '100%', display: 'block', maxHeight: '340px', objectFit: 'cover', objectPosition: 'top' }} />
                    </div>
                    <div className="p-5" style={{ backgroundColor: '#0a0d18' }}>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: s.color }}>Verdict</p>
                      <p className="text-sm font-bold text-white mb-1">{s.verdict}</p>
                      <p className="text-xs mb-3" style={{ color: '#888' }}>{s.detail}</p>
                      <div className="flex items-start gap-2 px-3 py-2 rounded-xl" style={{ backgroundColor: `${s.color}10`, border: `1px solid ${s.color}25` }}>
                        <span className="text-xs font-semibold flex-shrink-0" style={{ color: s.color }}>Next fix:</span>
                        <span className="text-xs" style={{ color: '#aaa' }}>{s.fix}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-4 mt-5">
                    <button onClick={prev} className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor='rgba(255,255,255,0.25)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor='rgba(255,255,255,0.1)'}>
                      <ChevronLeft className="w-4 h-4" style={{ color: '#666' }} />
                    </button>
                    <div className="flex gap-1.5">
                      {slides.map((_, i) => (
                        <button key={i} onClick={() => setCarouselActive(i)} className="rounded-full transition-all"
                          style={{ width: i === carouselActive ? '20px' : '6px', height: '6px', backgroundColor: i === carouselActive ? s.color : 'rgba(255,255,255,0.15)' }} />
                      ))}
                    </div>
                    <button onClick={next} className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor='rgba(255,255,255,0.25)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor='rgba(255,255,255,0.1)'}>
                      <ChevronRight className="w-4 h-4" style={{ color: '#666' }} />
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Divider */}
            <div className="mb-8 md:mb-14" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} />

            {/* From emotional to systematic */}
            <div className="text-center mb-6 md:mb-12">
              <p className="text-xs font-semibold mb-2 md:mb-4 uppercase tracking-widest" style={{ color: AC }}>Your path</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">From emotional to systematic</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {[
                { step: '01', when: 'Day 1',     color: AC,        title: 'First trade',         desc: 'Set your risk profile and place your first virtual trade.' },
                { step: '02', when: 'Week 1–4',  color: '#a78bfa', title: 'Build a baseline',    desc: 'Build a baseline Discipline Score from real decisions.' },
                { step: '03', when: 'Month 1–2', color: '#c084fc', title: 'Find the leaks',      desc: 'Find repeated behavior leaks: stop-loss, R:R, frequency, revenge trades.' },
                { step: '04', when: 'Month 2–3', color: '#e879f9', title: 'Tighten your rules',  desc: 'Use Nyx feedback to tighten your rules and build consistent habits.' },
              ].map((j, i) => (
                <div key={j.step} className="fade-in-up" style={{ animationDelay: `${i * 0.12}s` }}>
                  <p className="text-[10px] sm:text-xs font-bold mb-1.5" style={{ color: j.color, filter: `drop-shadow(0 0 8px ${j.color}88)` }}>{j.when}</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-black mb-1.5 md:mb-3" style={{ color: j.color, lineHeight: 1, filter: `drop-shadow(0 0 12px ${j.color})` }}>{j.step}</p>
                  <h3 className="font-semibold text-white mb-1 md:mb-2 text-xs sm:text-sm">{j.title}</h3>
                  <p className="text-xs sm:text-sm" style={{ color: '#999', lineHeight: 1.65 }}>{j.desc}</p>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ════ TESTIMONIALS — commented out until real user quotes are collected ════
        <section className="py-16 md:py-24 px-4 sm:px-6" style={{ background: 'rgba(0,0,0,0.3)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 md:mb-14">
              <p className="text-xs font-semibold mb-2 md:mb-4 uppercase tracking-widest" style={{ color: AC }}>What traders say</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ lineHeight: 1.2 }}>
                Discipline scores don't lie.
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  name: 'Arjun M.',
                  role: 'Retail trader, 2 years',
                  quote: 'I used to cut my stop losses every other trade. After 3 weeks on TradeReflex, Nyx flagged it 11 times. I finally saw the pattern I was blind to.',
                  before: 34, after: 71,
                },
                {
                  name: 'Priya S.',
                  role: 'Beginner, 6 months',
                  quote: "The discipline score shook me. I thought I was trading well. 38/100 on my first week. Now I actually plan my R:R before every entry — it's become a reflex.",
                  before: 38, after: 79,
                },
                {
                  name: 'Kiran R.',
                  role: 'Swing trader, 4 years',
                  quote: 'Backtesting + Nyx AI together is a different level. I ran my strategy 90 days back, found 3 pattern flaws, fixed them. My win rate went from 38% to 51%.',
                  before: 52, after: 86,
                },
              ].map(t => (
                <div key={t.name} className="card-hover p-4 sm:p-6 rounded-2xl flex flex-col"
                  style={{ backgroundColor: '#0a1020', border: '1px solid rgba(255,255,255,0.07)', transition: 'all 0.22s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(99,102,241,0.35)'; e.currentTarget.style.boxShadow='0 0 24px rgba(99,102,241,0.1)'; e.currentTarget.style.transform='translateY(-3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.07)'; e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='translateY(0)'; }}>
                  <div className="flex mb-2 sm:mb-3">
                    {Array.from({length:5}).map((_,i) => <Star key={i} className="star-glow w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color:'#fbbf24', fill:'#fbbf24' }} />)}
                  </div>
                  <p className="text-xs sm:text-sm flex-1 mb-4 sm:mb-6" style={{ color: '#999', lineHeight: 1.75 }}>"{t.quote}"</p>
                  <div className="flex items-center justify-between pt-3 sm:pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div>
                      <p className="text-sm font-semibold text-white">{t.name}</p>
                      <p className="text-xs" style={{ color: '#555' }}>{t.role}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-bold">
                      <span style={{ color: '#EF4444' }}>{t.before}</span>
                      <ArrowRight className="w-3 h-3" style={{ color: '#555' }} />
                      <span style={{ color: '#16C784' }}>{t.after}</span>
                      <span style={{ color: '#555' }}>score</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        ════ END TESTIMONIALS ════ */}

        {/* ════ SUBSCRIPTION PLANS ════ */}
        <section id="pricing" className="py-16 md:py-24 px-4 sm:px-6" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.4) 100%)', borderTop: '1px solid rgba(99,102,241,0.12)', borderBottom: '1px solid rgba(99,102,241,0.08)' }}>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8 md:mb-14">
              <p className="text-xs font-semibold mb-2 md:mb-4 uppercase tracking-widest" style={{ color: AC }}>Pricing</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ lineHeight: 1.2 }}>
                Two plans. No confusion.
              </h2>
              <p className="mt-2 md:mt-3 text-xs sm:text-sm" style={{ color: '#777' }}>Start free. Upgrade when you're ready to go all-in.</p>

              {/* Billing toggle */}
              <div className="flex items-center justify-center gap-1 mt-6 p-1 rounded-xl mx-auto"
                style={{ backgroundColor: '#0f0f0f', border: '1px solid rgba(255,255,255,0.07)', display: 'inline-flex' }}>
                {[
                  { key: 'monthly', short: '1mo',  long: 'Monthly'  },
                  { key: '3mo',     short: '3mo',  long: '3 Months', badge: 'POPULAR',  badgeColor: '#16C784' },
                  { key: '6mo',     short: '6mo',  long: '6 Months', badge: 'SAVE 25%', badgeColor: AC        },
                  { key: '1yr',     short: '1yr',  long: '1 Year',   badge: 'SAVE 33%', badgeColor: '#f59e0b' },
                ].map(opt => (
                  <button key={opt.key} onClick={() => setBillingPeriod(opt.key)}
                    className="relative rounded-lg font-semibold px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm"
                    style={{
                      backgroundColor: billingPeriod === opt.key ? AC2 : 'transparent',
                      color: billingPeriod === opt.key ? '#fff' : '#666',
                      transition: 'all 0.15s',
                      whiteSpace: 'nowrap',
                    }}>
                    <span className="sm:hidden">{opt.short}</span>
                    <span className="hidden sm:inline">
                      {opt.long}
                      {opt.badge && (
                        <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: `${opt.badgeColor}20`, color: opt.badgeColor }}>
                          {opt.badge}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Two-card grid */}
            <div className="grid md:grid-cols-2 gap-5">

              {/* Free */}
              <div className="card-hover p-6 rounded-2xl flex flex-col"
                style={{ backgroundColor: '#0a1020', border: '1px solid rgba(255,255,255,0.07)', transition: 'all 0.22s ease' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(99,102,241,0.3)'; e.currentTarget.style.boxShadow='0 0 24px rgba(99,102,241,0.08)'; e.currentTarget.style.transform='translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.07)'; e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='translateY(0)'; }}>
                <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#777' }}>Free</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-black text-white">₹0</span>
                </div>
                <p className="text-xs mb-6" style={{ color: '#666' }}>Forever free — no card needed</p>
                <ul className="space-y-3 flex-1 mb-6">
                  {[
                    '₹10L virtual capital',
                    'Equity trading (51+ NSE stocks)',
                    'Basic discipline score',
                    '20 trades per month',
                    'ACT scenarios (12 exercises)',
                    'Risk profile setup',
                    'Nyx pre-trade check (5 msg/week)',
                    'Markets, Global Cues & Regime',
                  ].map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm" style={{ color: '#666' }}>
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(99,102,241,0.12)' }}>
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: AC }} />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => go('/signup')}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold"
                  style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#aaa', background: 'transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.2)'; e.currentTarget.style.color='#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.1)'; e.currentTarget.style.color='#aaa'; }}>
                  Get Started Free
                </button>
              </div>

              {/* Premium — highlighted */}
              <div className="p-6 rounded-2xl flex flex-col relative"
                style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.13) 0%, rgba(99,102,241,0.04) 100%)', border: '1px solid rgba(99,102,241,0.45)', animation: 'subtlePulse 3s ease-in-out infinite' }}>
                <div className="hidden md:block absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[13px] font-bold text-white"
                  style={{ backgroundColor: AC2 }}>RECOMMENDED</div>
                <div className="flex md:hidden justify-center mb-3">
                  <span className="px-3 py-0.5 rounded-full text-[13px] font-bold text-white" style={{ backgroundColor: AC2 }}>RECOMMENDED</span>
                </div>
                <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: AC }}>Premium</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-black text-white">
                    { { monthly:'₹599', '3mo':'₹500', '6mo':'₹450', '1yr':'₹400' }[billingPeriod] }
                  </span>
                  <span className="text-sm" style={{ color: '#777' }}>/mo</span>
                </div>
                <p className="text-xs mb-6" style={{ color: '#666' }}>
                  { {
                    monthly: 'Billed monthly',
                    '3mo':   '₹1,499 total · billed every 3 months',
                    '6mo':   '₹2,699 total · billed every 6 months',
                    '1yr':   '₹4,799 total · billed annually',
                  }[billingPeriod] }
                </p>
                <ul className="space-y-3 flex-1 mb-6">
                  {[
                    'Everything in Free',
                    'F&O trading — options & futures',
                    'Unlimited trades per month',
                    'Full discipline score (9 components)',
                    'Nyx AI Coach (150 msg/week)',
                    'Behavior & Trade Analytics',
                    'Market Scanners (27+)',
                    'Algo Flows & Backtesting',
                    'PDF & Email trade reports',
                  ].map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm" style={{ color: '#aaa' }}>
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(99,102,241,0.2)' }}>
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: AC }} />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => handleCheckout('premium')}
                  disabled={checkoutLoading}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold text-white"
                  style={{ backgroundColor: AC2, opacity: checkoutLoading ? 0.7 : 1, cursor: checkoutLoading ? 'wait' : 'pointer' }}
                  onMouseEnter={e => { if (!checkoutLoading) e.currentTarget.style.backgroundColor='#4f46e5'; }}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor=AC2}>
                  {checkoutLoading ? 'Loading…' : 'Get Premium'}
                </button>
              </div>
            </div>

            {/* Nudge line */}
            <p className="mt-5 text-xs text-center" style={{ color: 'rgba(255,255,255,0.35)' }}>
              No credit card needed to start — sign up free and explore the full app.
            </p>

            {/* Beta notice */}
            <div className="mt-6 flex items-center justify-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fbbf24', display: 'inline-block', animation: 'subtlePulse 2s ease-in-out infinite' }} />
                Beta
              </span>
              <p className="text-xs" style={{ color: '#555' }}>
                TradeReflex is currently open to <span style={{ color: '#888' }}>beta users only</span>. Official launch coming soon.
              </p>
            </div>
          </div>
        </section>

        {/* ════ FINAL CTA ════ */}
        <section className="py-16 md:py-24 px-4 sm:px-6" style={{ background: 'rgba(0,0,0,0.3)', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-3xl mx-auto text-center" style={{ position: 'relative' }}>
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at center, rgba(99,102,241,0.18) 0%, rgba(99,102,241,0.06) 40%, transparent 70%)', filter: 'blur(20px)' }} />
            <p className="text-xs font-semibold mb-4 uppercase tracking-widest" style={{ color: AC }}>Start today</p>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-6"
              style={{ lineHeight: 1.15, letterSpacing: '-0.02em' }}>
              See where discipline<br />can take your trading.
            </h2>
            <p className="mb-6 md:mb-8 text-sm sm:text-base md:text-lg" style={{ color: '#777' }}>
              The first trading tool you'll actually learn from.<br />
              And the last bad habit you'll repeat.
            </p>
            {/* 3 trust chips */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
              {['₹10L virtual capital, zero risk','Real NSE prices, real conditions','Cancel anytime'].map(t => (
                <span key={t} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
                  style={{ backgroundColor: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', color: AC }}>
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#16C784' }} />{t}
                </span>
              ))}
            </div>
            <button onClick={() => go('/signup')}
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 font-semibold text-white text-sm sm:text-base"
              style={{
                borderRadius: '9999px',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
                border: '1px solid rgba(99,102,241,0.4)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 0 24px rgba(99,102,241,0.3), 0 4px 20px rgba(0,0,0,0.4)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(99,102,241,0.7)'; e.currentTarget.style.boxShadow='inset 0 1px 0 rgba(255,255,255,0.16), 0 0 40px rgba(99,102,241,0.45), 0 6px 24px rgba(0,0,0,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(99,102,241,0.4)'; e.currentTarget.style.boxShadow='inset 0 1px 0 rgba(255,255,255,0.12), 0 0 24px rgba(99,102,241,0.3), 0 4px 20px rgba(0,0,0,0.4)'; }}>
              Start Free — ₹10L Virtual Capital <ArrowRight className="w-5 h-5" />
            </button>
            <p className="mt-4 text-xs" style={{ color: '#555' }}>No credit card. No real money. Just discipline.</p>
            {/* social proof strip — re-enable once real user count is confirmed */}
          </div>
        </section>

        {/* ════ FAQ ════ */}
        <section id="faq" className="py-16 md:py-24 px-4 sm:px-6" style={{ background: 'rgba(255,255,255,0.012)' }}>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-6 md:mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Frequently asked</h2>
            </div>
            <div className="space-y-2.5">
              {[
                { q: 'Is this a real trading platform?',
                  a: 'No. TradeReflex is a paper trading simulator. You practice with ₹10L of virtual capital on NSE stocks. No real trades are placed and no real money is involved.' },
                { q: 'Do I risk any real money?',
                  a: 'No. All capital on TradeReflex is virtual. There are no payment requirements to trade. You cannot lose real money here.' },
                { q: 'Is the market data live?',
                  a: 'TradeReflex uses real historical NSE OHLCV data replayed in a simulated session. You trade on actual price and volume data, but it is not a live feed. No live orders are placed.' },
                { q: 'Do you provide trading tips or buy/sell calls?',
                  a: 'No. TradeReflex does not give buy or sell recommendations. Nyx AI gives feedback on your behavior and decision quality — not on which stocks to pick. All trade decisions are yours.' },
                { q: 'What is the Discipline Score?',
                  a: 'A 0–100 score built from 9 behavioral components: stop-loss discipline, risk-reward planning, strategy consistency, emotional control, trade frequency, risk profile adherence, confidence alignment, journal discipline, and SL violations. It updates after every session.' },
                { q: 'What does Nyx AI do?',
                  a: 'Nyx reads your real trade history, ACT answers, risk profile, and Discipline Score. Before a trade it checks your R:R, stop-loss, and position size. After a trade it gives a short debrief with one specific fix for the next session. It does not give stock tips.' },
                { q: 'Do I need trading experience?',
                  a: 'No. Beginners can use the learning module, ACT scenarios, and risk profile setup to build foundational habits. Experienced traders use the Discipline Score and Nyx feedback to identify and fix existing behavioral leaks.' },
                { q: 'Can I start for free?',
                  a: 'Yes. You can sign up and start trading with virtual capital immediately. No payment or card is required to use the core platform.' },
              ].map((item, i) => (
                <div key={i} className="rounded-xl overflow-hidden"
                  style={{ border: faqOpen === i ? '1px solid rgba(99,102,241,0.35)' : '1px solid rgba(255,255,255,0.06)', boxShadow: faqOpen === i ? '0 0 20px rgba(99,102,241,0.1)' : 'none', transition: 'all 0.25s ease' }}>
                  <button onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 text-left"
                    style={{ backgroundColor: faqOpen === i ? 'rgba(99,102,241,0.06)' : '#0f0f0f', transition: 'background 0.2s' }}>
                    <span className="text-sm font-medium text-white pr-4">{item.q}</span>
                    {faqOpen === i
                      ? <ChevronUp   className="icon-glow-indigo w-4 h-4 flex-shrink-0" style={{ color: AC }} />
                      : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: '#666' }} />}
                  </button>
                  {faqOpen === i && (
                    <div className="px-4 pb-4" style={{ backgroundColor: 'rgba(99,102,241,0.04)' }}>
                      <p className="text-sm" style={{ color: '#aaa', lineHeight: 1.85 }}>{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════ FOOTER ════ */}
        <footer className="py-10 px-4 sm:px-6" style={{ background: 'rgba(0,0,0,0.35)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <Logo size="xs" />
            <p className="text-xs text-center" style={{ color: '#555' }}>
              Virtual trading only · No real money involved
            </p>
            <div className="flex items-center gap-6">
              {[['Sign In','/login'],['Get Started','/signup'],['Privacy','/privacy'],['Terms','/terms']].map(([l,p]) => (
                <button key={l} onClick={() => go(p)} className="text-xs" style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer' }}
                  onMouseEnter={e => e.target.style.color='#888'}
                  onMouseLeave={e => e.target.style.color='#444'}>{l}</button>
              ))}
            </div>
          </div>
        </footer>

        </div>{/* end relative zIndex:1 content wrapper */}

        {/* Checkout error toast */}
        {checkoutError && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl text-sm font-semibold text-white flex items-center gap-3"
            style={{ backgroundColor: '#1a0a0a', border: '1px solid rgba(239,68,68,0.5)', boxShadow: '0 4px 24px rgba(239,68,68,0.2)' }}>
            <span style={{ color: '#ef4444' }}>⚠</span>
            {checkoutError}
            <button onClick={() => setCheckoutError('')} style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 4 }}>✕</button>
          </div>
        )}

    </div>
  );
}










